import React, { useState, useEffect, useContext, useRef } from "react";
import ReactEcharts from "echarts-for-react";
import { SizeContext } from "layouts/AppMain";
import "./index.css";

export type DataType = {
  name: string;
  value: number;
};

export interface LineProps {
  data: DataType[];
}

const getOption = (
  data: DataType[] = [],
  s: (n: number, b?: boolean) => number
) => {
  const option = {
    grid: {
      top: "10%",
      left: s(56),
      right: s(24),
      bottom: s(36),
    },
    tooltip: {
      alwaysShowContent: true,
      trigger: "axis",
      position: (point: number[]) => [point[0] + 12, point[1] - 10],
      padding: 0,
      backgroundColor: "tranparent",
      axisPointer: {
        lineStyle: {
          type: "dashed",
          color: "#489abb",
        },
      },
      formatter: (p: any[]) =>
        `<div class="relative line-tooltip">${p[0].value.toLocaleString()}</div>`,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      axisLabel: {
        show: true,
        color: "#676ba5",
        fontSize: s(16),
        formatter: (v: string) => v.slice(11, 16),
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#353ca5",
        },
      },
      data: data.map((d) => d.name),
    },
    yAxis: {
      type: "value",
      axisLabel: {
        show: true,
        fontSize: s(16),
        color: "#676ba5",
        formatter: (v: number) => `${(v / 100000000).toFixed(1)}亿`,
        showMinLabel: false,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      splitLine: {
        lineStyle: {
          color: "#353ca5",
        },
      },
    },
    series: [
      {
        type: "line",
        smooth: true,
        lineStyle: {
          color: "#7cc2fa",
        },
        symbolSize: 1,
        symbol: "circle",
        itemStyle: {
          color: "tranparent",
          emphasis: {
            color: "#fff",
          },
        },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: "rgba(85, 93, 208, .95)",
              },
              {
                offset: 1,
                color: "rgba(85, 93, 208, 0)",
              },
            ],
          },
        },
        data: data.map((d) => d.value),
      },
    ],
  };

  return option;
};

const Line: React.FC<LineProps> = ({ data }) => {
  const getSize = useContext(SizeContext);
  const [option, setOption] = useState<any>({});
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const myChart = (ref.current as any).getEchartsInstance();

    const timer = setTimeout(() => {
      // 显示 tooltip
      myChart.dispatchAction({
        type: "showTip",
        seriesIndex: 0,
        dataIndex: current + 1,
      });
      setCurrent((current + 1) % (data?.length ?? 1));
    }, 1800);

    return () => clearTimeout(timer);
  }, [current, data]);

  useEffect(() => {
    setOption(getOption(data, getSize));
  }, [data, getSize]);

  return (
    <ReactEcharts
      option={option}
      notMerge={true}
      ref={ref}
      style={{ width: "100%", height: "100%", position: "absolute" }}
    />
  );
};

export default Line;
